// Benepack OMS · Sidebar (WF-02 §2.1)
// SSOT: docs/ui/sitemap.md §2 · 8 顶级模块 + 二级菜单 · permission 过滤
// 跟 wireframes §2 ASCII 严格一致: 200px / 收起 80px / #001529 dark / Menu inline dark
import { Badge, Layout, Menu, Space } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMemo, type ReactNode } from 'react';
import {
  DashboardOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  DollarOutlined,
  DatabaseOutlined,
  AppstoreOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import { usePendingApprovals } from '@/hooks/usePendingApprovals';
import { Logo } from '@/components/Logo';

const { Sider } = Layout;

/** 菜单项标签 + 待办徽标 (审批中心 PO 待审数 · count=0 时 AntD Badge 自动隐藏 → 无侵入)。 */
function MenuLabelWithBadge({ label, count }: { label: ReactNode; count: number }) {
  return (
    <Space size={8} style={{ justifyContent: 'space-between', width: '100%' }}>
      <span>{label}</span>
      <Badge count={count} size="small" overflowCount={99} />
    </Space>
  );
}

// 内部递归节点 (带 permission 元数据 · 过滤后再转 AntD MenuProps['items'])
interface Node {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  permAny?: string[];
  children?: Node[];
}

type AntdMenuItem = NonNullable<MenuProps['items']>[number];

export interface SidebarProps {
  collapsed: boolean;
  onCollapse: (c: boolean) => void;
}

function toAntdItem(n: Node): AntdMenuItem {
  if (n.children && n.children.length) {
    return {
      key: n.key,
      label: n.label,
      icon: n.icon,
      children: n.children.map(toAntdItem),
    } as AntdMenuItem;
  }
  return { key: n.key, label: n.label, icon: n.icon } as AntdMenuItem;
}

export function Sidebar({ collapsed, onCollapse }: SidebarProps) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const { hasAnyPerm } = useAuth();
  // 审批中心待办徽标 (go-live 反馈 #3 · 三面之一 · 仅 PO 待审数 · 共享 usePendingApprovals 单轮询)。
  const { poCount } = usePendingApprovals();

  // 全量菜单树 (跟 sitemap §2)
  const fullItems: Node[] = useMemo(
    () => [
      { key: '/', icon: <DashboardOutlined />, label: t('menu.dashboard') },
      // report-page (P2a Task 3): 统计报表 · 顶级菜单 (任一指标 read 权限即见 · 与 routes.tsx
      // PermGuardRoute anyOf 同 5 码 · 页内卡片级再 hasPerm 细分)
      {
        key: '/reports',
        icon: <BarChartOutlined />,
        label: t('menu.reports'),
        permAny: [
          'ar:read',
          'ap:read',
          'invoice_register:read',
          'sales_order:read',
          'purchase_order:read',
        ],
      },
      {
        key: '/sales',
        icon: <ShoppingOutlined />,
        label: t('menu.sales'),
        permAny: ['quotation:read', 'sales_order:read', 'customer:read'],
        children: [
          // D-4.2c-1 (rc7): Quotation vertical slice 落地 · key 对齐真实 route /trade/quotations (api path /trade/*)
          {
            key: '/trade/quotations',
            label: t('menu.salesQuotations'),
            permAny: ['quotation:read'],
          },
          // D-4.2a (rc4): SO 复合页落地 · key 对齐真实 route /trade/sales-orders (api path /trade/*)
          {
            key: '/trade/sales-orders',
            label: t('menu.salesOrders'),
            permAny: ['sales_order:read'],
          },
          { key: '/master/customers', label: t('menu.salesCustomers'), permAny: ['customer:read'] },
        ],
      },
      {
        key: '/procurement',
        icon: <ShoppingCartOutlined />,
        label: t('menu.procurement'),
        permAny: ['purchase_order:read'],
        children: [
          // D-4.2b-2 (rc5a): PO 审批复合页落地 · key 对齐真实 route /trade/purchase-orders (api path /trade/*)
          {
            key: '/trade/purchase-orders',
            label: t('menu.procurementPo'),
            permAny: ['purchase_order:read'],
          },
          {
            key: '/procurement/approvals',
            // 待办徽标: PO 待审数 (0 自动隐藏 · 收起态 AntD 仍渲染但不抢宽 · 见 MenuLabelWithBadge)。
            label: <MenuLabelWithBadge label={t('menu.procurementApprovals')} count={poCount} />,
            permAny: ['purchase_order:approve'],
          },
        ],
      },
      {
        key: '/fulfillment',
        icon: <TruckOutlined />,
        label: t('menu.fulfillment'),
        permAny: ['shipment:read', 'receive_note:read', 'batch:read', 'coa:read'],
        children: [
          // WP-07 (rc9): Shipment 复合页落地 · key 对齐真实 route /trade/shipments (api path /trade/*)
          {
            key: '/trade/shipments',
            label: t('menu.fulfillmentShipments'),
            permAny: ['shipment:read'],
          },
          // WP-08 (rc10): ReceiveNote 复合页落地 · key 对齐真实 route /trade/receive-notes
          {
            key: '/trade/receive-notes',
            label: t('menu.fulfillmentReceiveNotes'),
            permAny: ['receive_note:read'],
          },
          {
            key: '/fulfillment/batches',
            label: t('menu.fulfillmentBatches'),
            permAny: ['batch:read'],
          },
          { key: '/fulfillment/coa', label: t('menu.fulfillmentCoa'), permAny: ['coa:read'] },
        ],
      },
      {
        key: '/finance',
        icon: <DollarOutlined />,
        label: t('menu.finance'),
        permAny: ['statement:read', 'invoice_register:read', 'ar:read', 'ap:read'],
        children: [
          {
            key: '/finance/statements',
            label: t('menu.financeStatements'),
            permAny: ['statement:read'],
          },
          // WP-18 (rc14): InvoiceRegister 发票登记 vertical slice 落地 · 替换原 /finance/invoices 占位
          //   (旧占位 key=/finance/invoices + perm invoice:read 均不存在 · 死链从不渲染)
          {
            key: '/finance/invoice-registers',
            label: t('menu.financeInvoiceRegisters'),
            permAny: ['invoice_register:read'],
          },
          { key: '/finance/ar', label: t('menu.financeAr'), permAny: ['ar:read'] },
          { key: '/finance/ap', label: t('menu.financeAp'), permAny: ['ap:read'] },
        ],
      },
      {
        key: '/master',
        icon: <DatabaseOutlined />,
        label: t('menu.master'),
        permAny: ['customer:read', 'supplier:read', 'product:read'],
        children: [
          {
            key: '/master/customers',
            label: t('menu.masterCustomers'),
            permAny: ['customer:read'],
          },
          {
            key: '/master/suppliers',
            label: t('menu.masterSuppliers'),
            permAny: ['supplier:read'],
          },
          { key: '/master/products', label: t('menu.masterProducts'), permAny: ['product:read'] },
          { key: '/master/skus', label: t('menu.masterSkus'), permAny: ['sku:read'] },
          {
            key: '/master/price-lists',
            label: t('menu.masterPriceLists'),
            permAny: ['price_list:read'],
          },
          {
            // P2-1 (PRD-2) · 定价三层接线 · 客户折扣维护
            key: '/master/customer-discounts',
            label: t('menu.masterCustomerDiscounts'),
            permAny: ['customer_discount:read'],
          },
        ],
      },
      {
        key: '/platform',
        icon: <AppstoreOutlined />,
        label: t('menu.platform'),
        // ★ audit-slice (rc23): perm code 修正 audit:read → audit_log:read (latent typo · 实测 alice JWT
        //   permissions 含 audit_log:read·无 audit:read → 原写法菜单恒隐·route guard 同步 audit_log:read)。
        permAny: [
          'email_job:read',
          'audit_log:read',
          'approval:approve',
          'approval:reject',
          'purchase_order:approve',
        ],
        children: [
          // WP-B · 统一审批中心 (PO + 外发邮件) · anyOf 同 route guard (邮件审批人 Eileen 可见)
          {
            key: '/platform/approvals',
            label: t('menu.platformApprovals'),
            permAny: ['approval:approve', 'approval:reject', 'purchase_order:approve'],
          },
          { key: '/platform/email', label: t('menu.platformEmail'), permAny: ['email_job:read'] },
          // WP-D · 邮件签收看板 + 签收 TTL 配置 (PDF 邮件确认签收 · 均 RBAC email_job:read 可见 ·
          //   TTL 改写需 company_profile:write·页内 hasPerm 守门·菜单可见门用 email_job:read)。
          {
            key: '/platform/email/receipts',
            label: t('menu.platformEmailReceipts'),
            permAny: ['email_job:read'],
          },
          {
            key: '/platform/email/receipt-ttl',
            label: t('menu.platformEmailReceiptTtl'),
            permAny: ['email_job:read'],
          },
          { key: '/platform/audit', label: t('menu.platformAudit'), permAny: ['audit_log:read'] },
        ],
      },
      {
        key: '/system',
        icon: <TeamOutlined />,
        label: t('menu.system'),
        permAny: ['user:read', 'role:read', 'company_profile:write'],
        children: [
          { key: '/system/users', label: t('menu.systemUsers'), permAny: ['user:read'] },
          { key: '/system/roles', label: t('menu.systemRoles'), permAny: ['role:read'] },
          // pdf-branding · 企业信息设置 (公司抬头/品牌/Logo) · RBAC company_profile:write = admin
          {
            key: '/system/settings',
            label: t('menu.systemSettings'),
            permAny: ['company_profile:write'],
          },
          // 账户安全 (2FA WP · 自助管理二次验证) · ★无 permAny: 自助页·每个登录用户管自己的 2FA。
          //   故意不挂权限门 → 始终可见 (route 同样仅 RequireAuth 守门·见 auth/routes.tsx security)。
          //   副作用: 本项无 permAny → filterNode 恒保留 → 系统组对无 user:read/role:read 的非管理员也可见
          //   (符合预期: 任何人都该能进自己的账户安全设置)。
          { key: '/system/security', label: t('menu.systemSecurity') },
        ],
      },
    ],
    // poCount: 审批中心徽标随待审数变化 (primitive number · 稳定 dep · 不引死循环)。
    [t, poCount],
  );

  // 按权限过滤 (顶级模块若所有子项都不可见 · 整个模块隐藏)
  const items = useMemo<AntdMenuItem[]>(() => {
    const filterNode = (n: Node): Node | null => {
      if (n.children && n.children.length) {
        const kids = n.children.map(filterNode).filter((x): x is Node => x !== null);
        if (kids.length === 0) return null;
        return { ...n, children: kids };
      }
      if (n.permAny && !hasAnyPerm(n.permAny)) return null;
      return n;
    };
    return fullItems
      .map(filterNode)
      .filter((x): x is Node => x !== null)
      .map(toAntdItem);
  }, [fullItems, hasAnyPerm]);

  // 当前选中 + 默认展开的父级
  const selectedKey = loc.pathname;
  const openKey = '/' + loc.pathname.split('/').filter(Boolean)[0];

  return (
    <Sider
      width={200}
      collapsedWidth={80}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      theme="dark"
      style={{ minHeight: '100vh' }}
    >
      <div
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: collapsed ? 0 : '0 16px',
          background: '#000C17',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Logo variant={collapsed ? 'mark' : 'sidebar'} height={28} />
      </div>
      <Menu
        theme="dark"
        mode="inline"
        items={items}
        selectedKeys={[selectedKey]}
        defaultOpenKeys={[openKey]}
        onClick={({ key }) => nav(String(key))}
      />
    </Sider>
  );
}
